export interface AutocompleteConfig {
  url?: string;
  searchTermPlaceholder?: string;
  onFetch?: (query: string) => Promise<AutocompleteResponse>;
  minChars: number;
  resultListClass: string;
  selector: string;
  onSelect?: (selectedItem: AutocompleteItem) => void;
  closeOnSelect?: boolean;
  maxResults?: number;
  fetchOnFocus?: boolean;
  defaultQuery?: string;
  highlightResults?: boolean;
  additionalParameters?: string;
  debounceTime?: number;
}

export interface AutocompleteItem {
  name: string;
  type?: string;
  id?: string;
}

interface AutocompleteResponse {
  query: string;
  results: AutocompleteItem[];
}

export class Autocomplete {
  private readonly config: AutocompleteConfig;
  private readonly hostElement: HTMLElement;
  private readonly input: HTMLInputElement;
  private resultsContainer: HTMLUListElement | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private debounceTimer: NodeJS.Timeout | null = null;

  constructor(config: AutocompleteConfig, hostElement: HTMLElement, input: HTMLInputElement) {
    this.config = config;
    this.hostElement = hostElement;
    this.input = input;
  }

  public init(): void {
    this.addEventListeners();
    this.setupResizeObserver();
  }

  private addEventListeners(): void {
    this.input.addEventListener('input', this.handleInput);
    this.input.addEventListener('keydown', this.handleKeydown);
    this.input.addEventListener('blur', this.handleBlur);
    this.input.addEventListener('focus', this.handleFocus);
    document.addEventListener('click', this.handleClickOutside);
    window.addEventListener('resize', this.handleResize);
  }

  private setupResizeObserver(): void {
    this.resizeObserver = new ResizeObserver(this.handleResize);
    const containerParent = this.getContainerParent();
    this.resizeObserver.observe(containerParent);
  }

  private readonly handleInput = (): void => {
    const query = this.input.value.trim();

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      query ? this.getSuggestions(query) : this.clearResults();
    }, this.config.debounceTime || 400);
  };

  private readonly handleKeydown = (event: KeyboardEvent): void => {
    if (!this.resultsContainer) return;

    const items = this.resultsContainer.querySelectorAll('li');
    let activeIndex = Array.from(items).findIndex((item) => item.classList.contains('active'));

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        activeIndex = this.handleArrowKeys(event.key, activeIndex, items.length);
        this.setActiveItem(items, activeIndex);
        event.preventDefault();
        break;
      case 'Enter':
        // only continue if activeIndex there is an active item
        if (activeIndex !== -1) {
          this.handleEnterKey(activeIndex, items);
        }
        break;
      case 'Escape':
        this.clearResults();
        break;
    }
  };

  private readonly handleArrowKeys = (
    key: string,
    activeIndex: number,
    itemsLength: number
  ): number => {
    return key === 'ArrowDown'
      ? (activeIndex + 1) % itemsLength
      : (activeIndex - 1 + itemsLength) % itemsLength;
  };

  private readonly handleEnterKey = (activeIndex: number, items: NodeListOf<Element>): void => {
    if (activeIndex !== -1) {
      const selectedItem = items[activeIndex];
      this.handleItemSelection({
        name: selectedItem.textContent || '',
        type: selectedItem.getAttribute('data-type') || '',
      });
    }
  };

  private readonly handleBlur = (): void => {
    setTimeout(() => this.clearResults(), 200);
  };

  private readonly handleFocus = (): void => {
    const inputValue = this.input.value.trim();
    if (this.config.fetchOnFocus) {
      const query = inputValue || this.config.defaultQuery || '';
      this.getSuggestions(query);
    } else if (inputValue) {
      this.getSuggestions(inputValue);
    }
  };

  private readonly handleClickOutside = (event: MouseEvent): void => {
    if (!this.hostElement.shadowRoot?.contains(event.target as Node)) {
      this.clearResults();
    }
  };

  private readonly handleResize = (): void => {
    if (this.resultsContainer) {
      const containerParent = this.getContainerParent();
      this.setResultsContainerStyles(this.resultsContainer, containerParent);
    }
  };

  private readonly getSuggestions = async (query: string): Promise<AutocompleteResponse> => {
    if (!this.config.fetchOnFocus && query.length < this.config.minChars) {
      this.clearResults();
      return { query, results: [] };
    }

    try {
      let data: AutocompleteResponse;

      if (this.config.onFetch) {
        // Use the custom fetch function if provided
        data = await this.config.onFetch(query);
      } else if (this.config.url) {
        // Fall back to the default fetch method if url is provided
        const url = this.config.url.replace(
          this.config.searchTermPlaceholder,
          encodeURIComponent(query) + (this.config.additionalParameters || '')
        );
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        data = await response.json();
      } else {
        throw new Error('No fetch function or URL provided in the configuration');
      }

      data.results.length > 0 ? this.displayResults(data) : this.clearResults();
      return data;
    } catch (error) {
      console.error('Error fetching autocomplete suggestions:', error);
      this.clearResults();
      return { query, results: [] };
    }
  };

  private displayResults(data: AutocompleteResponse): void {
    const containerParent = this.getContainerParent();
    this.clearResults();
    if (data.results.length === 0) return;

    this.resultsContainer = this.createResultsContainer(containerParent);
    // Apply maxResults limit
    const limitedResults = this.config.maxResults
      ? data.results.slice(0, this.config.maxResults)
      : data.results;
    this.populateResultsContainer(limitedResults);
    this.updateInputAttributes();
  }

  private getContainerParent(): HTMLElement {
    const containerParent = this.hostElement.shadowRoot?.querySelector(this.config.selector);
    if (!containerParent) {
      throw new Error(`Container parent element not found: ${this.config.selector}`);
    }
    return containerParent as HTMLElement;
  }

  private createResultsContainer(containerParent: HTMLElement): HTMLUListElement {
    const resultsContainer = document.createElement('ul');
    resultsContainer.className = this.config.resultListClass;
    resultsContainer.setAttribute('role', 'listbox');
    resultsContainer.id = 'autocomplete-results';
    resultsContainer.setAttribute('tabindex', '-1'); // Add this line
    this.setResultsContainerStyles(resultsContainer, containerParent);
    containerParent.insertAdjacentElement('afterend', resultsContainer);
    return resultsContainer;
  }

  private setResultsContainerStyles(container: HTMLUListElement, parent: HTMLElement): void {
    const parentWidth = parent.getBoundingClientRect().width;
    container.style.boxSizing = 'border-box';
    container.style.maxWidth = `${parentWidth}px`;
    container.style.width = '100%';
  }

  private populateResultsContainer(results: AutocompleteItem[]): void {
    const inputValue = this.input.value.trim();
    const highlightRegex = new RegExp(`(${this.escapeRegExp(inputValue)})`, 'gi');

    results.forEach((result, index) => {
      const resultItem = document.createElement('li');

      if (this.config.highlightResults) {
        resultItem.innerHTML = result.name.replace(highlightRegex, '<mark>$1</mark>');
      } else {
        resultItem.textContent = result.name;
      }

      resultItem.setAttribute('role', 'option');
      resultItem.setAttribute('data-type', result.type);
      resultItem.id = `autocomplete-item-${index}`;
      resultItem.addEventListener('click', () => this.handleItemSelection(result));
      this.resultsContainer?.appendChild(resultItem);
    });
  }

  private escapeRegExp(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private updateInputAttributes(): void {
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-controls', 'autocomplete-results');
  }

  private handleItemSelection(selectedItem: AutocompleteItem): void {
    if (this.config.onSelect) {
      this.config.onSelect(selectedItem);
    }

    this.input.value = selectedItem.name;

    if (this.config.closeOnSelect) {
      this.clearResults();
    }
  }

  private setActiveItem(items: NodeListOf<Element>, activeIndex: number): void {
    items.forEach((item, index) => {
      item.classList.toggle('active', index === activeIndex);
      if (index === activeIndex) {
        (item as HTMLElement).scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    });
  }

  private clearResults(): void {
    this.resultsContainer?.remove();
    this.resultsContainer = null;
  }

  public destroy(): void {
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('click', this.handleClickOutside);
    this.input.removeEventListener('input', this.handleInput);
    this.input.removeEventListener('keydown', this.handleKeydown);
    this.input.removeEventListener('blur', this.handleBlur);
    this.input.removeEventListener('focus', this.handleFocus);
    this.resizeObserver?.disconnect();
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.clearResults();
    this.resultsContainer = null;
    this.resizeObserver = null;
    this.debounceTimer = null;
  }
}
